import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import {
  Phone, MapPin, Clock, Star, Mail, Smile, Sparkles, ShieldCheck, BadgeCheck,
  Calendar, CalendarCheck, ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown,
  Check, Menu, X, Heart, Users, Stethoscope, Baby, CreditCard, Activity,
} from 'lucide-react';

const SERVICE_ICONS = {
  'Preventive Care': ShieldCheck,
  'Cosmetic Dentistry': Sparkles,
  'Restorative': Activity,
  'Family-Friendly': Heart,
};

const PILLARS = [
  {
    icon: CalendarCheck,
    title: 'Same-day appointments',
    body: 'Hold slots every day for new patients and emergencies. Cracked a crown? Toothache? Call before noon, see us today.',
  },
  {
    icon: Users,
    title: 'One family. One practice.',
    body: 'Babies through grandparents, all in the same chair-friendly office. Same hygienist for years — kids actually look forward to coming.',
  },
  {
    icon: BadgeCheck,
    title: 'Pricing in writing, before we start',
    body: "Insurance verified before your visit. No surprise charges, no commission selling, no upsell scripts. The number on the quote is the number on the bill.",
  },
];

const INSURANCE_PROVIDERS = [
  'Delta Dental', 'Blue Cross', 'Cigna', 'MetLife', 'Aetna', 'United Healthcare',
  'Guardian', 'Humana', 'Principal', 'Ameritas',
];

const SMILE_JOURNEY = [
  {
    week: 'Week 0',
    title: 'Free 3D scan',
    body: '15-minute appointment. No goop, no impressions — we use a wand-style scanner and show you a preview of your finished smile right there on the screen.',
  },
  {
    week: 'Week 1–2',
    title: 'Custom aligners arrive',
    body: 'Lab-fabricated trays cut to your treatment plan. Pickup visit takes 20 minutes — we show you how to wear them and talk through the daily rhythm.',
  },
  {
    week: 'Month 1–9',
    title: 'Switch trays at home',
    body: 'Most patients change trays every 1–2 weeks. Quick check-ins every 6–8 weeks (you can even do these by video). Floss like normal, eat like normal.',
  },
  {
    week: 'Final visit',
    title: 'Reveal + retainer',
    body: 'Polish, photos, and a custom retainer that keeps everything in place. Average treatment is 6 months for mild cases, 12 months for complex.',
  },
];

const FAQ_DATA = [
  { q: 'What insurance do you accept?', a: 'Most major plans including Delta Dental, Blue Cross, Cigna, MetLife, Aetna, and United. Call us with your card and we\'ll verify benefits before your visit — no surprises at checkout.' },
  { q: 'Do you offer financing?', a: 'Yes — CareCredit and our in-house plan, both with 0% APR for qualifying treatment up to 24 months. We\'ll talk through it transparently in the consult.' },
  { q: 'Are you taking new patients?', a: 'Yes — kids and adults. Most new-patient appointments book 1–2 weeks out; emergencies get same-day priority.' },
  { q: 'Do you handle emergencies?', a: 'Same-day for existing patients, next-day for new. Call the office and press 1 — front desk routes urgent calls directly.' },
  { q: 'Is sedation available for anxious patients?', a: 'Yes — nitrous oxide ("laughing gas") for mild anxiety, and oral conscious sedation for bigger procedures. Both are very common; bring a driver if you choose oral.' },
  { q: 'How early should kids start coming?', a: 'First visit by age 1 or within 6 months of the first tooth, whichever comes first. The early visits are mostly about getting comfortable with the chair — fast, fun, and free for the parent.' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// Scoped CSS for sparkle drift, smile-arc, sheen on the primary CTA. Single
// inject — class prefix `rvd-` keeps it from colliding with other demos.
const RVD_STYLE = `
  @keyframes rvd-sheen { 0% { transform: translateX(-120%); } 60%, 100% { transform: translateX(220%); } }
  .rvd-cta-sheen { position: relative; overflow: hidden; isolation: isolate; }
  .rvd-cta-sheen::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%);
    transform: translateX(-120%);
    animation: rvd-sheen 5.5s ease-in-out infinite;
    animation-delay: 1.4s;
    pointer-events: none;
  }
  @keyframes rvd-float {
    0%   { transform: translateY(8px) rotate(0deg); opacity: 0; }
    18%  { opacity: var(--rvd-op, 0.6); }
    82%  { opacity: var(--rvd-op, 0.6); }
    100% { transform: translateY(-90vh) rotate(360deg); opacity: 0; }
  }
  .rvd-float { animation: rvd-float var(--rvd-dur, 14s) linear infinite; }
  @keyframes rvd-twinkle {
    0%, 100% { opacity: 0.35; transform: scale(0.9); }
    50%      { opacity: 1; transform: scale(1.15); }
  }
  .rvd-twinkle { animation: rvd-twinkle 3.4s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .rvd-cta-sheen::after, .rvd-float, .rvd-twinkle { animation: none !important; }
  }
`;

function useCountUp(target, { active, duration = 1400 } = {}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (target === 0) { setValue(0); return undefined; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return value;
}

function parseStat(value) {
  const m = String(value).replace(/,/g, '').match(/^(-?\d+(?:\.\d+)?)(.*)$/);
  if (!m) return null;
  return { num: parseFloat(m[1]), suffix: m[2] || '' };
}

function ToothMark({ size = 18, className = '' }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" className={className}>
      <path
        d="M12 2.6c-2.7 0-3.7 1.4-5.5 1.4S3 5.6 3 8.5c0 2 .9 3.5 1.6 5.4.6 1.7.4 3.5 1.2 5.6.6 1.6 1.7 2.5 2.6 1.7.9-.7.7-2.5 1.3-4.6.4-1.4 1-2.1 2.3-2.1s1.9.7 2.3 2.1c.6 2.1.4 3.9 1.3 4.6.9.8 2-.1 2.6-1.7.8-2.1.6-3.9 1.2-5.6.7-1.9 1.6-3.4 1.6-5.4 0-2.9-1.7-4.5-3.5-4.5S14.7 2.6 12 2.6Z"
        fill="currentColor"
      />
    </svg>
  );
}

function RiversideDentalDemo({ demo, sample = false }) {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [tIndex, setTIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  const phoneRaw = demo.contact.phone.replace(/[^\d+]/g, '');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!demo.testimonials?.length) return undefined;
    const id = setInterval(() => {
      setTIndex((i) => (i + 1) % demo.testimonials.length);
    }, 7000);
    return () => clearInterval(id);
  }, [demo.testimonials]);

  return (
    <div
      className="bg-white text-slate-900"
      style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
    >
      <style>{RVD_STYLE}</style>
      <SudcoBanner sample={sample} demoName={demo.name} demoIndustry={demo.industry} />
      <TopBar phone={demo.contact.phone} phoneRaw={phoneRaw} address={demo.contact.address} hours={demo.hours} />
      <Header
        demoName={demo.name}
        phone={demo.contact.phone}
        phoneRaw={phoneRaw}
        scrolled={scrolled}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        fontDisplay={demo.fontDisplay}
      />
      <Hero demo={demo} fontDisplay={demo.fontDisplay} />
      <Stats stats={demo.stats} fontDisplay={demo.fontDisplay} />
      <InsuranceStrip />
      <Pillars fontDisplay={demo.fontDisplay} />
      <Services services={demo.services} fontDisplay={demo.fontDisplay} />
      <Doctors about={demo.about} fontDisplay={demo.fontDisplay} />
      <SmileJourney fontDisplay={demo.fontDisplay} />
      <Pricing featured={demo.featured} fontDisplay={demo.fontDisplay} />
      <Gallery images={demo.gallery} fontDisplay={demo.fontDisplay} />
      <Testimonials
        testimonials={demo.testimonials}
        index={tIndex}
        setIndex={setTIndex}
        fontDisplay={demo.fontDisplay}
      />
      <FaqSection openFaq={openFaq} setOpenFaq={setOpenFaq} fontDisplay={demo.fontDisplay} />
      <CtaBanner phone={demo.contact.phone} phoneRaw={phoneRaw} fontDisplay={demo.fontDisplay} />
      <Contact contact={demo.contact} hours={demo.hours} fontDisplay={demo.fontDisplay} />
      <Footer demo={demo} fontDisplay={demo.fontDisplay} />
    </div>
  );
}

function SudcoBanner({ sample, demoName, demoIndustry }) {
  return (
    <div className="fixed bottom-5 right-5 z-50">
      <div className="bg-[#0B0F19]/90 border border-[#2A3142] backdrop-blur-md rounded-full px-5 py-3 shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)] flex items-center gap-4 text-sm">
        <span className="text-[#8B92A8] hidden sm:inline">
          {sample
            ? <>Like this? Get one for your <span className="text-[#F5F6FA] font-medium">{demoIndustry || 'business'}</span> — <span className="text-[#6366F1] font-semibold">$800</span></>
            : <>Free demo for <span className="text-[#F5F6FA] font-medium">{demoName}</span> by <span className="text-[#6366F1] font-semibold">Sudco Solutions</span></>
          }
        </span>
        <Link to="/" className="flex items-center gap-1.5 text-[#F5F6FA] font-semibold hover:text-[#06B6D4] transition-colors whitespace-nowrap">
          <ArrowLeft size={14} /> Back to Sudco
        </Link>
      </div>
    </div>
  );
}

function TopBar({ phone, phoneRaw, address, hours }) {
  const todayLine = hours?.[0];
  return (
    <div className="hidden md:block bg-cyan-900 text-cyan-100 text-xs">
      <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={12} className="text-cyan-300" />{address}
          </span>
          {todayLine && (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} className="text-cyan-300" />{todayLine.day}: {todayLine.time}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-cyan-300" />Most insurance accepted
          </span>
        </div>
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <CalendarCheck size={12} className="text-cyan-300" />Now welcoming new patients
          </span>
          <a href={`tel:${phoneRaw}`} className="inline-flex items-center gap-1.5 text-white font-semibold hover:text-cyan-200">
            <Phone size={12} className="text-cyan-300" />{phone}
          </a>
        </div>
      </div>
    </div>
  );
}

function Header({ demoName, phone, phoneRaw, scrolled, navOpen, setNavOpen, fontDisplay }) {
  const links = [
    { href: '#services', label: 'Services' },
    { href: '#doctors', label: 'Our Doctors' },
    { href: '#journey', label: 'Invisalign' },
    { href: '#pricing', label: 'Offers' },
    { href: '#reviews', label: 'Reviews' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur shadow-[0_1px_0_0_rgba(0,0,0,0.06),0_8px_24px_-12px_rgba(0,0,0,0.12)]'
          : 'bg-white border-b border-slate-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-700 flex items-center justify-center text-white shadow-sm">
            <ToothMark size={18} />
          </span>
          <span
            className="font-extrabold text-lg md:text-xl tracking-tight text-cyan-950"
            style={{ fontFamily: fontDisplay }}
          >
            {demoName}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold text-slate-700">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="relative group">
              <span className="group-hover:text-cyan-700 transition-colors">{l.label}</span>
              <span className="absolute left-0 -bottom-1 h-0.5 w-full origin-left scale-x-0 bg-cyan-600 transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${phoneRaw}`}
            className="inline-flex items-center gap-2 text-cyan-700 font-bold text-base hover:text-cyan-900"
          >
            <Phone size={16} />{phone}
          </a>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold shadow-sm hover:shadow-md transition-all"
          >
            Book a visit <ArrowRight size={14} />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          className="lg:hidden p-2 -mr-2 text-slate-700"
        >
          {navOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {navOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-slate-100 bg-white"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setNavOpen(false)}
                  className="px-3 py-2.5 rounded-md font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <a
                  href={`tel:${phoneRaw}`}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-cyan-600 text-cyan-700 text-sm font-bold"
                >
                  <Phone size={14} />Call
                </a>
                <a
                  href="#contact"
                  onClick={() => setNavOpen(false)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-cyan-600 text-white text-sm font-bold"
                >
                  Book
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function SparkleField() {
  const items = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        left: `${(i * 53) % 100}%`,
        size: 6 + ((i * 13) % 14),
        delay: -((i * 1.7) % 14),
        dur: 12 + ((i * 7) % 10),
        op: 0.45 + ((i * 11) % 4) * 0.1,
        hue: i % 3 === 0 ? '#22D3EE' : i % 3 === 1 ? '#34D399' : '#7DD3FC',
      })),
    [],
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {items.map((p, i) => (
        <span
          key={i}
          className="rvd-float absolute"
          style={{
            left: p.left,
            bottom: '-10%',
            width: p.size,
            height: p.size,
            color: p.hue,
            '--rvd-dur': `${p.dur}s`,
            '--rvd-op': p.op,
            animationDelay: `${p.delay}s`,
          }}
        >
          <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor">
            <path d="M12 2 L13.6 9 L21 10.5 L13.6 12 L12 19 L10.4 12 L3 10.5 L10.4 9 Z" />
          </svg>
        </span>
      ))}
    </div>
  );
}

function SmileArc({ flip = false, className = '' }) {
  return (
    <svg
      viewBox="0 0 1440 80"
      preserveAspectRatio="none"
      className={`block w-full h-12 md:h-16 ${className}`}
      style={flip ? { transform: 'scaleY(-1)' } : undefined}
      aria-hidden="true"
    >
      <path
        d="M0,40 C360,90 1080,-10 1440,40 L1440,80 L0,80 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Hero({ demo, fontDisplay }) {
  const [form, setForm] = useState({ name: '', phone: '', when: 'morning', reason: 'cleaning' });
  const [submitted, setSubmitted] = useState(false);

  function onSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setSubmitted(true);
  }

  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 90% 70% at 80% -10%, #CFFAFE 0%, transparent 60%), radial-gradient(ellipse 70% 60% at 0% 40%, #ECFDF5 0%, transparent 65%), linear-gradient(180deg, #F0F9FF 0%, #FFFFFF 100%)',
      }}
    >
      <SparkleField />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-12 pb-20 md:pt-20 md:pb-28">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-5"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Now welcoming new patients · {demo.location}
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-extrabold text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-cyan-950 mb-5"
              style={{ fontFamily: fontDisplay }}
            >
              Gentle, modern dentistry.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-emerald-500">
                Smiles for the whole family.
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
              {demo.tagline} Same-day crowns, transparent pricing, and a team that's been here for nearly two decades.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                  ))}
                </span>
                <strong className="text-slate-900">4.9</strong> · 480+ reviews
              </span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-cyan-600" /> Most insurance accepted</span>
              <span className="inline-flex items-center gap-1.5"><CalendarCheck size={14} className="text-cyan-600" /> Same-day emergencies</span>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a
                href="#contact"
                className="rvd-cta-sheen inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-base font-bold shadow-lg shadow-cyan-900/20 transition-all hover:scale-[1.02]"
              >
                <Calendar size={18} />Book a visit
              </a>
              <a
                href={`tel:${demo.contact.phone.replace(/[^\d+]/g, '')}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-white hover:bg-slate-50 text-cyan-700 text-base font-semibold border border-cyan-200 shadow-sm"
              >
                <Phone size={18} />{demo.contact.phone}
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-10 flex items-center gap-4">
              <div className="flex -space-x-3">
                {['#22D3EE', '#34D399', '#7DD3FC'].map((c, i) => (
                  <span
                    key={c}
                    className="w-9 h-9 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: c }}
                  >
                    {['JL', 'CR', 'PO'][i]}
                  </span>
                ))}
              </div>
              <p className="text-sm text-slate-500 leading-snug max-w-xs">
                Trusted by 12,000+ Boise neighbors. <span className="text-slate-700 font-semibold">"My six-year-old now asks when she goes back."</span>
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-cyan-200/60 via-emerald-100/50 to-sky-200/40 blur-xl" aria-hidden="true" />
            <div className="relative bg-white rounded-2xl shadow-xl shadow-cyan-900/10 p-6 md:p-7 border border-cyan-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-cyan-700">Request a visit</span>
                <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <Clock size={12} /> Replies in &lt; 1 hr
                </span>
              </div>
              <h2
                className="font-extrabold text-2xl mb-4 leading-tight text-cyan-950"
                style={{ fontFamily: fontDisplay }}
              >
                Find a chair that fits you.
              </h2>

              {submitted ? (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center mb-3">
                    <Check size={22} />
                  </div>
                  <p className="font-bold text-emerald-900 mb-1">We've got it, {form.name.split(' ')[0]}.</p>
                  <p className="text-sm text-emerald-800">
                    A real person will call {form.phone} this {form.when === 'morning' ? 'morning' : form.when === 'afternoon' ? 'afternoon' : 'evening'} to confirm your appointment. No phone tag.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setSubmitted(false); setForm({ name: '', phone: '', when: 'morning', reason: 'cleaning' }); }}
                    className="mt-4 text-sm text-emerald-700 font-semibold hover:text-emerald-900"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Your name</span>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jenna L."
                      className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Phone</span>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(208) 555-0117"
                      className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none text-sm"
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-700">Best time</span>
                      <select
                        value={form.when}
                        onChange={(e) => setForm({ ...form, when: e.target.value })}
                        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none text-sm bg-white"
                      >
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">After 4pm</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-700">Visit type</span>
                      <select
                        value={form.reason}
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none text-sm bg-white"
                      >
                        <option value="cleaning">Cleaning / checkup</option>
                        <option value="cosmetic">Cosmetic / Invisalign</option>
                        <option value="emergency">Tooth pain</option>
                        <option value="kids">Kids visit</option>
                        <option value="other">Something else</option>
                      </select>
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="rvd-cta-sheen w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-bold shadow-md hover:shadow-lg transition-all"
                  >
                    Request my appointment <ArrowRight size={16} />
                  </button>
                  <p className="text-[11px] text-slate-500 text-center">
                    No commitment. We confirm by phone within an hour, then you pick the time.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="text-white">
        <SmileArc />
      </div>
    </section>
  );
}

function Stats({ stats, fontDisplay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  if (!stats?.length) return null;
  return (
    <section ref={ref} className="bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s, i) => (
            <StatCard key={s.label} stat={s} active={inView} fontDisplay={fontDisplay} delay={i * 0.05} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ stat, active, fontDisplay, delay }) {
  const parsed = parseStat(stat.value);
  const numeric = useCountUp(parsed?.num ?? 0, { active });
  const display = parsed
    ? `${parsed.num >= 1000 ? Math.round(numeric).toLocaleString() : numeric.toFixed(parsed.num % 1 ? 1 : 0)}${parsed.suffix}`
    : stat.value;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-white p-5 md:p-6 text-center"
    >
      <div
        className="font-extrabold text-3xl md:text-4xl text-cyan-900 leading-none"
        style={{ fontFamily: fontDisplay }}
      >
        {display}
      </div>
      <div className="mt-2 text-xs md:text-sm font-semibold text-slate-600 uppercase tracking-wider">
        {stat.label}
      </div>
    </motion.div>
  );
}

function InsuranceStrip() {
  return (
    <section className="bg-slate-50 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-4 md:gap-8">
          <div className="md:max-w-xs">
            <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-1">In-network with</p>
            <p className="text-sm text-slate-600">
              We verify benefits before your visit. Don't see your plan? Call us — we work with most.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {INSURANCE_PROVIDERS.map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs md:text-sm font-semibold text-slate-700 shadow-sm"
              >
                <ShieldCheck size={12} className="text-cyan-600" />{p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillars({ fontDisplay }) {
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Why Riverside</p>
          <h2
            className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            The dental experience your last office couldn't quite figure out.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative rounded-2xl border border-slate-100 bg-white p-7 hover:shadow-xl hover:shadow-cyan-900/10 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="absolute top-0 left-7 right-7 h-1 rounded-b-full bg-gradient-to-r from-cyan-500 to-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="inline-flex w-12 h-12 rounded-xl bg-cyan-50 text-cyan-700 items-center justify-center mb-5 group-hover:bg-cyan-600 group-hover:text-white transition-colors">
                <p.icon size={22} />
              </span>
              <h3
                className="font-extrabold text-xl text-cyan-950 mb-2"
                style={{ fontFamily: fontDisplay }}
              >
                {p.title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-[15px]">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services({ services, fontDisplay }) {
  return (
    <section id="services" className="relative py-16 md:py-24 bg-cyan-50/40">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">What we do</p>
            <h2
              className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight"
              style={{ fontFamily: fontDisplay }}
            >
              Full-service care, all under one roof.
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              From a six-month-old's first checkup to grandma's same-day crown — we keep referrals to a minimum and your time in the chair short.
            </p>
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-sm font-bold text-cyan-700 hover:text-cyan-900"
          >
            See all services <ArrowRight size={14} />
          </a>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => {
            const Icon = SERVICE_ICONS[s.title] || Smile;
            return (
              <motion.a
                key={s.title}
                href="#contact"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="group relative rounded-2xl bg-white p-6 border border-slate-100 hover:border-cyan-300 hover:shadow-lg transition-all overflow-hidden"
              >
                <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-gradient-to-br from-cyan-100 to-emerald-100 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <span className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 text-white items-center justify-center mb-4 shadow-sm">
                    <Icon size={20} />
                  </span>
                  <h3
                    className="font-bold text-lg text-cyan-950 mb-1.5 group-hover:text-cyan-700 transition-colors"
                    style={{ fontFamily: fontDisplay }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-cyan-700 opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight size={12} />
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DoctorAvatar({ initials, color }) {
  return (
    <svg viewBox="0 0 200 240" className="w-full h-auto" aria-hidden="true">
      <defs>
        <linearGradient id={`bg-${initials}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color[0]} />
          <stop offset="100%" stopColor={color[1]} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="200" height="240" rx="16" fill={`url(#bg-${initials})`} />
      <circle cx="100" cy="92" r="40" fill="rgba(255,255,255,0.95)" />
      <path d="M40,210 C40,165 70,140 100,140 C130,140 160,165 160,210 Z" fill="rgba(255,255,255,0.95)" />
      <text
        x="100"
        y="100"
        textAnchor="middle"
        fontSize="36"
        fontWeight="800"
        fontFamily="Manrope, sans-serif"
        fill={color[1]}
      >
        {initials}
      </text>
      <g opacity="0.85">
        <circle cx="35" cy="35" r="3" fill="white" />
        <circle cx="170" cy="55" r="2" fill="white" />
        <circle cx="180" cy="180" r="2.5" fill="white" />
        <circle cx="25" cy="175" r="2" fill="white" />
      </g>
    </svg>
  );
}

function Doctors({ about, fontDisplay }) {
  const docs = [
    {
      name: 'Dr. Anjali Patel, DDS',
      role: 'Co-founder · General & Cosmetic',
      initials: 'AP',
      color: ['#22D3EE', '#0E7490'],
      bio: 'University of Washington, ’03. Eighteen years in practice, 4,000+ Invisalign cases. Speaks English, Hindi, and a passable amount of Mandarin.',
      tags: ['Invisalign Diamond+', 'CEREC certified', 'Conscious sedation'],
    },
    {
      name: 'Dr. Ben Chen, DDS',
      role: 'Co-founder · Pediatric & Restorative',
      initials: 'BC',
      color: ['#34D399', '#047857'],
      bio: 'Creighton University, ’05. Pediatric residency at Children\'s Hospital Colorado. The chair-side TVs were his idea — kids love him for it.',
      tags: ['Pediatric specialty', 'Implants & crowns', 'Sleep apnea'],
    },
  ];

  return (
    <section id="doctors" className="relative py-16 md:py-24 bg-white overflow-hidden">
      <div className="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-cyan-50/60 to-transparent" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Meet your doctors</p>
          <h2
            className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight mb-4"
            style={{ fontFamily: fontDisplay }}
          >
            {about?.heading || 'Two doctors. One philosophy.'}
          </h2>
          {about?.body?.split('\n\n').map((para, i) => (
            <p key={i} className="text-slate-600 leading-relaxed mb-3 text-[15px]">{para}</p>
          ))}
          {about?.signature && (
            <p
              className="mt-4 text-cyan-800 font-semibold italic"
              style={{ fontFamily: fontDisplay }}
            >
              — {about.signature}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {docs.map((d, i) => (
            <motion.div
              key={d.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="rounded-2xl bg-gradient-to-br from-white to-cyan-50/40 border border-cyan-100 p-6 md:p-7 flex gap-5 md:gap-6"
            >
              <div className="w-28 md:w-32 flex-shrink-0">
                <DoctorAvatar initials={d.initials} color={d.color} />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className="font-extrabold text-xl text-cyan-950 leading-tight"
                  style={{ fontFamily: fontDisplay }}
                >
                  {d.name}
                </h3>
                <p className="text-cyan-700 text-sm font-semibold mt-0.5">{d.role}</p>
                <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{d.bio}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {d.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-100 text-cyan-800 text-[11px] font-bold"
                    >
                      <Stethoscope size={10} />{t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SmileJourney({ fontDisplay }) {
  const [active, setActive] = useState(0);
  const reduceMotion = useReducedMotion();

  return (
    <section
      id="journey"
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #F0F9FF 0%, #ECFEFF 100%)' }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Your Invisalign timeline</p>
            <h2
              className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight mb-5"
              style={{ fontFamily: fontDisplay }}
            >
              From first scan to final reveal — exactly what to expect.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6 text-[15px]">
              Most patients come in skeptical and leave with a clear plan and a real number. Free consult, 0% APR, and a Diamond+ provider doing the work — not someone who took a weekend course.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-cyan-200 text-xs font-bold text-cyan-800">
                <Sparkles size={12} className="text-cyan-600" />Diamond+ Provider
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-cyan-200 text-xs font-bold text-cyan-800">
                <CreditCard size={12} className="text-cyan-600" />0% APR financing
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-cyan-200 text-xs font-bold text-cyan-800">
                <Heart size={12} className="text-cyan-600" />Adults & teens
              </span>
            </div>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-bold shadow-md hover:shadow-lg transition-all"
            >
              Book my free scan <ArrowRight size={16} />
            </a>
          </div>

          <div className="relative">
            <div
              className="absolute left-[19px] md:left-[23px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-cyan-200 via-emerald-200 to-cyan-100"
              aria-hidden="true"
            />
            <div className="space-y-4 md:space-y-5">
              {SMILE_JOURNEY.map((step, i) => {
                const isActive = active === i;
                return (
                  <button
                    key={step.title}
                    type="button"
                    onClick={() => setActive(i)}
                    onMouseEnter={() => !reduceMotion && setActive(i)}
                    className={`w-full text-left relative pl-12 md:pl-16 pr-5 py-5 rounded-2xl border transition-all ${
                      isActive
                        ? 'bg-white border-cyan-300 shadow-lg shadow-cyan-900/10'
                        : 'bg-white/60 border-slate-100 hover:bg-white hover:border-cyan-200'
                    }`}
                  >
                    <span
                      className={`absolute left-3 md:left-4 top-5 w-9 md:w-11 h-9 md:h-11 rounded-full flex items-center justify-center text-xs font-extrabold transition-all ${
                        isActive
                          ? 'bg-cyan-600 text-white shadow-md scale-110'
                          : 'bg-white border-2 border-cyan-200 text-cyan-700'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-600">
                        {step.week}
                      </span>
                      {isActive && (
                        <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">
                          · You are here
                        </span>
                      )}
                    </div>
                    <h3
                      className={`font-extrabold text-lg md:text-xl leading-tight mt-1 ${
                        isActive ? 'text-cyan-950' : 'text-slate-800'
                      }`}
                      style={{ fontFamily: fontDisplay }}
                    >
                      {step.title}
                    </h3>
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.p
                          initial={{ height: 0, opacity: 0, marginTop: 0 }}
                          animate={{ height: 'auto', opacity: 1, marginTop: 8 }}
                          exit={{ height: 0, opacity: 0, marginTop: 0 }}
                          transition={{ duration: 0.25 }}
                          className="text-slate-600 text-[15px] leading-relaxed overflow-hidden"
                        >
                          {step.body}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing({ featured, fontDisplay }) {
  if (!featured?.items?.length) return null;
  return (
    <section id="pricing" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-12">
          {featured.kicker && (
            <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">{featured.kicker}</p>
          )}
          <h2
            className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            {featured.heading || 'Special offers'}
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Real prices, written down. We'll verify your benefits before your first visit so the number you see is the number you pay.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.items.map((item, i) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className={`relative rounded-2xl border p-6 transition-all hover:-translate-y-1 hover:shadow-xl ${
                item.tag
                  ? 'border-cyan-300 bg-gradient-to-br from-cyan-50 to-white shadow-md shadow-cyan-900/5'
                  : 'border-slate-100 bg-white hover:border-cyan-200'
              }`}
            >
              {item.tag && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-cyan-600 text-white text-[11px] font-extrabold uppercase tracking-wider shadow">
                  <Sparkles size={11} />{item.tag}
                </span>
              )}
              <h3
                className="font-extrabold text-lg text-cyan-950 leading-tight pr-4"
                style={{ fontFamily: fontDisplay }}
              >
                {item.name}
              </h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed min-h-[3.5rem]">{item.desc}</p>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-end justify-between gap-3">
                <span
                  className="font-extrabold text-2xl text-cyan-700 leading-none"
                  style={{ fontFamily: fontDisplay }}
                >
                  {item.price}
                </span>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-1 text-xs font-bold text-cyan-700 hover:text-cyan-900"
                >
                  Book this <ArrowRight size={12} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery({ images, fontDisplay }) {
  const [lightbox, setLightbox] = useState(null);
  if (!images?.length) return null;

  const captions = ['Operatory 1 — chair-side TVs', 'Same-day crown mill', 'Family waiting room'];

  return (
    <section className="py-16 md:py-24 bg-cyan-50/40">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Inside the practice</p>
          <h2
            className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Modern equipment, calm rooms, comfortable chairs.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {images.map((src, i) => (
            <motion.button
              key={src}
              type="button"
              onClick={() => setLightbox(i)}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className={`group relative rounded-2xl overflow-hidden bg-cyan-100 ${
                i === 0 ? 'col-span-2 md:row-span-2 md:col-span-2 aspect-[4/3] md:aspect-auto' : 'aspect-square'
              }`}
            >
              <img
                src={src}
                alt={captions[i] || ''}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-cyan-950/70 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-3 left-3 right-3 text-white text-xs md:text-sm font-semibold">
                {captions[i] || ''}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-cyan-950/90 backdrop-blur flex items-center justify-center p-4 md:p-8"
            onClick={() => setLightbox(null)}
          >
            <motion.img
              key={images[lightbox]}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              src={images[lightbox]}
              alt={captions[lightbox] || ''}
              className="max-w-5xl max-h-[80vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white text-sm">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="font-semibold">{lightbox + 1} / {images.length}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Testimonials({ testimonials, index, setIndex, fontDisplay }) {
  if (!testimonials?.length) return null;
  const total = testimonials.length;
  const t = testimonials[index];
  return (
    <section id="reviews" className="py-16 md:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Patients say</p>
        <h2
          className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight mb-2"
          style={{ fontFamily: fontDisplay }}
        >
          480+ five-star reviews. Here are three.
        </h2>
        <div className="flex items-center justify-center gap-1 mb-10">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={18} className="fill-amber-400 text-amber-400" />
          ))}
          <span className="ml-2 text-slate-600 font-semibold">4.9 on Google</span>
        </div>

        <div className="relative min-h-[220px] md:min-h-[200px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0"
            >
              <p
                className="text-xl md:text-2xl leading-relaxed text-cyan-950 max-w-3xl mx-auto"
                style={{ fontFamily: fontDisplay }}
              >
                "{t.quote}"
              </p>
              <footer className="mt-6 text-sm text-slate-600 font-semibold">
                — {t.author}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + total) % total)}
            aria-label="Previous review"
            className="w-10 h-10 rounded-full border border-slate-300 hover:border-cyan-500 hover:text-cyan-700 flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-1.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Review ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-6 bg-cyan-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % total)}
            aria-label="Next review"
            className="w-10 h-10 rounded-full border border-slate-300 hover:border-cyan-500 hover:text-cyan-700 flex items-center justify-center transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

function FaqSection({ openFaq, setOpenFaq, fontDisplay }) {
  return (
    <section id="faq" className="py-16 md:py-24 bg-slate-50 border-y border-slate-100">
      <div className="max-w-5xl mx-auto px-4 md:px-6 grid md:grid-cols-[1fr_1.4fr] gap-10 md:gap-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Common questions</p>
          <h2
            className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            We get these a lot — so here are the answers.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Don't see yours?{' '}
            <a href="#contact" className="text-cyan-700 font-bold underline underline-offset-4 hover:text-cyan-900">
              Send us a note
            </a>{' '}
            and we'll reply within an hour.
          </p>
        </div>
        <div className="space-y-2">
          {FAQ_DATA.map((item, i) => {
            const open = openFaq === i;
            return (
              <div
                key={item.q}
                className={`rounded-xl border transition-all ${
                  open ? 'bg-white border-cyan-200 shadow-sm' : 'bg-white border-slate-100'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  aria-expanded={open}
                >
                  <span className="font-bold text-cyan-950 text-[15px]">{item.q}</span>
                  <ChevronDown
                    size={18}
                    className={`flex-shrink-0 text-cyan-700 transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-slate-600 leading-relaxed text-[15px]">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBanner({ phone, phoneRaw, fontDisplay }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-cyan-700 via-cyan-600 to-emerald-500 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.18),transparent_55%)]" aria-hidden="true" />
      <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="text-center md:text-left max-w-xl">
          <h3
            className="font-extrabold text-2xl md:text-3xl leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Ready for a smile you'll actually want to show off?
          </h3>
          <p className="text-cyan-50 mt-2 text-base">
            New-patient exam, X-rays, and basic cleaning for $99 — through the end of the month.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <a
            href="#contact"
            className="rvd-cta-sheen inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white text-cyan-700 font-extrabold text-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all whitespace-nowrap"
          >
            <Calendar size={20} />Book my visit
          </a>
          <a
            href={`tel:${phoneRaw}`}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-800/30 hover:bg-cyan-800/50 backdrop-blur border border-white/20 text-white font-bold transition-colors whitespace-nowrap"
          >
            <Phone size={18} />{phone}
          </a>
        </div>
      </div>
    </section>
  );
}

function Contact({ contact, hours, fontDisplay }) {
  const phoneRaw = contact.phone.replace(/[^\d+]/g, '');
  return (
    <section id="contact" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Reach out</p>
          <h2
            className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            Three ways to get on the chair.
          </h2>
          <p className="text-slate-600 mb-7 leading-relaxed">
            Call, email, or send the form above — whichever fits your day. A real person on our front desk gets back to you within an hour during business hours.
          </p>

          <div className="space-y-4">
            <ContactRow
              icon={Phone}
              href={`tel:${phoneRaw}`}
              label="Call front desk"
              value={contact.phone}
            />
            <ContactRow
              icon={Mail}
              href={`mailto:${contact.email}`}
              label="Email"
              value={contact.email}
            />
            <ContactRow
              icon={MapPin}
              label="Office"
              value={contact.address}
            />
          </div>

          <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200">
            <div className="aspect-[16/9] bg-gradient-to-br from-cyan-100 via-emerald-50 to-sky-100 relative">
              <div className="absolute inset-0 opacity-60" style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, rgba(8,145,178,0.08) 0 1px, transparent 1px 28px), repeating-linear-gradient(90deg, rgba(8,145,178,0.08) 0 1px, transparent 1px 28px)',
              }} aria-hidden="true" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <span className="w-12 h-12 rounded-full bg-cyan-600 text-white flex items-center justify-center mb-3 shadow-lg">
                  <MapPin size={20} />
                </span>
                <p className="font-bold text-cyan-950">{contact.address}</p>
                <p className="text-sm text-slate-600 mt-1">Free parking · ground floor entry · wheelchair accessible</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-2">Hours</p>
          <h2
            className="font-extrabold text-3xl md:text-4xl text-cyan-950 leading-tight mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            When we're open.
          </h2>
          <div className="rounded-2xl border border-cyan-100 overflow-hidden">
            {hours.map((h, i) => (
              <div
                key={h.day}
                className={`flex items-center justify-between px-5 py-4 ${
                  i % 2 === 0 ? 'bg-cyan-50/40' : 'bg-white'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-semibold text-cyan-950">
                  <Clock size={16} className="text-cyan-600" />{h.day}
                </span>
                <span className="text-slate-600 font-medium">{h.time}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                <Baby size={18} />
              </span>
              <div>
                <p className="font-bold text-emerald-900 leading-tight">Bringing a kid?</p>
                <p className="text-sm text-emerald-800 mt-1 leading-relaxed">
                  We hold morning slots for first-visit kiddos. Bring snacks — Dr. Chen has a sticker drawer.
                </p>
              </div>
            </div>
          </div>

          <a
            href={`tel:${phoneRaw}`}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-bold shadow-md hover:shadow-lg transition-all"
          >
            <Phone size={16} />Call now: {contact.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, href, label, value }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <span className="w-11 h-11 flex-shrink-0 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-colors">
        <Icon size={18} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-0.5">{label}</div>
        <div className="text-cyan-950 font-semibold">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block">{inner}</a>
  ) : (
    <div>{inner}</div>
  );
}

function Footer({ demo, fontDisplay }) {
  return (
    <footer className="bg-cyan-950 text-cyan-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center text-cyan-950">
              <ToothMark size={16} />
            </span>
            <span
              className="font-extrabold text-lg text-white"
              style={{ fontFamily: fontDisplay }}
            >
              {demo.name}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-cyan-200/80 mb-4">
            Family dentistry in Boise since 2007. Same-day care, transparent pricing, gentle hands.
          </p>
          <div className="flex items-center gap-1 text-xs text-cyan-300">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={12} className="fill-amber-400 text-amber-400" />
            ))}
            <span className="ml-1.5 font-semibold">4.9 · 480+ Google reviews</span>
          </div>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
          <ul className="space-y-2 text-sm">
            {demo.services.map((s) => (
              <li key={s.title}>
                <a href="#services" className="hover:text-white transition-colors">{s.title}</a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Visit us</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <Phone size={14} className="text-cyan-300 mt-0.5 flex-shrink-0" />
              <a href={`tel:${demo.contact.phone.replace(/[^\d+]/g, '')}`} className="hover:text-white">{demo.contact.phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={14} className="text-cyan-300 mt-0.5 flex-shrink-0" />
              <a href={`mailto:${demo.contact.email}`} className="hover:text-white break-all">{demo.contact.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={14} className="text-cyan-300 mt-0.5 flex-shrink-0" />
              <span>{demo.contact.address}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-cyan-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-cyan-300/70">
          <p>&copy; {new Date().getFullYear()} {demo.name}. All rights reserved.</p>
          <p>
            Demo site built by{' '}
            <Link to="/" className="text-cyan-200 font-semibold hover:text-white">Sudco Solutions</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default RiversideDentalDemo;
